/**
 * Guarantees the quote signing link survives a wholesale body replacement.
 *
 * AI generation and template apply both replace the composer body outright, so a
 * draft that omits the "Review & Sign" link would ship a quote the customer
 * cannot sign. This re-asserts the link on the serialized Lexical state right
 * before it is committed to the editor.
 *
 * The appended nodes mirror what the backend's `convertHTMLToJSON` produces for
 * the prefill anchor `<p><br></p><p>You can review and sign the quote online
 * here: <a href="...">Review &amp; Sign</a></p>`, so the editor renders an
 * appended link identically to a prefilled one.
 */

import { logger } from '@commander/logger';

const LINK_INTRO_TEXT = 'You can review and sign the quote online here: ';
const LINK_TEXT = 'Review & Sign';

const createTextNode = (text: string) => ({
  detail: 0,
  format: 0,
  mode: 'normal',
  style: '',
  text,
  type: 'text',
  version: 1,
});

const createParagraphNode = (children: unknown[]) => ({
  children,
  direction: null,
  format: '',
  indent: 0,
  type: 'paragraph',
  version: 1,
  textFormat: 0,
  textStyle: '',
});

const createSigningLinkNodes = (signingUrl: string) => [
  createParagraphNode([]),
  createParagraphNode([
    createTextNode(LINK_INTRO_TEXT),
    {
      children: [createTextNode(LINK_TEXT)],
      direction: null,
      format: '',
      indent: 0,
      type: 'link',
      version: 1,
      rel: null,
      target: null,
      title: null,
      url: signingUrl,
    },
  ]),
];

/**
 * True when the tree already holds a link node pointing at `signingUrl`.
 *
 * The check is structural rather than a substring match on the serialized state:
 * generated bodies routinely carry the URL as plain text, which is not clickable
 * and must not suppress the appended anchor.
 */
const hasSigningLinkNode = (node: unknown, signingUrl: string): boolean => {
  if (Array.isArray(node)) return node.some((child) => hasSigningLinkNode(child, signingUrl));
  if (typeof node !== 'object' || node === null) return false;

  const candidate = node as { type?: unknown; url?: unknown; children?: unknown };
  if (candidate.type === 'link' && candidate.url === signingUrl) return true;

  return hasSigningLinkNode(candidate.children, signingUrl);
};

/**
 * Returns `body` with a signing-link paragraph appended, unless a link node for
 * the URL is already present, no URL was resolved, or the body is not a usable
 * Lexical state. Accepts a serialized state object or its JSON string and returns
 * the same representation it was given. Never throws — a failure leaves the
 * replacement body untouched.
 */
export function ensureSigningLink<T>(body: T, signingUrl: string | null | undefined): T {
  if (typeof signingUrl !== 'string' || signingUrl.length === 0) return body;

  const abandon = (reason: string): T => {
    logger.warn('[ensureSigningLink] Quote signing link was not asserted on the body', {
      reason,
      bodyType: typeof body,
    });
    return body;
  };

  try {
    const isJsonString = typeof body === 'string';
    if (!isJsonString && (typeof body !== 'object' || body === null)) {
      return abandon('body is not a Lexical state or JSON string');
    }

    // Parsing the serialized form also deep-copies it, so the caller's state is
    // never mutated in place.
    const state = isJsonString ? JSON.parse(body as string) : JSON.parse(JSON.stringify(body));
    if (!Array.isArray(state?.root?.children)) {
      return abandon('body has no root.children array');
    }

    if (hasSigningLinkNode(state.root.children, signingUrl)) return body;

    state.root.children.push(...createSigningLinkNodes(signingUrl));

    return (isJsonString ? JSON.stringify(state) : state) as T;
  } catch {
    return abandon('body could not be parsed');
  }
}
